import {
  CheckCircleIcon,
  PlayCircleIcon,
  CalendarDaysIcon,
  BeakerIcon,
  ShieldExclamationIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";

export const getRowStyle = (allergen, status, room, allergens) => {
  const rawAllergen = allergens ?? allergen;

  const normalizedAllergen = rawAllergen
    ? rawAllergen
        .split(", ")
        .map((a) => a.trim())
        .filter(Boolean)
        .map((a) => a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()) 
        .sort()
        .join(", ")
    : "None";

  const normalizedStatus = status
    ? status.replace(" ", "_").toUpperCase()
    : status;

  if (
    (room === "Room 1" || room === "Room 2") &&
    normalizedAllergen &&
    normalizedAllergen !== "None"
  ) {
    return {
      backgroundColor: "#ffe5e5", 
      border: "2px solid #dc2626",
    };
  }

  if (normalizedStatus === "COMPLETED") {
    return {
      backgroundColor: "#ffffff",
      border: "none",
    };
  }

  // Hold statuses
  if (normalizedStatus === "LAB_HOLD" || normalizedStatus === "LAB HOLD") {
    return {
      backgroundColor: "#fef08a",
      border: "none",
    };
  }
  if (normalizedStatus === "QC_HOLD" || normalizedStatus === "QC HOLD") {
    return {
      backgroundColor: "#f1bd99",
      border: "none",
    };
  }

  // Allergen colors with normalized keys (title case)
  const allergenColors = {
    Mustard: "#ffd9cc",
    Sesame: "#bce453",
    Celery: "#60a5fa",
    "Mustard, Sesame": "#fb923c",
    "Mustard, Celery": "#a78bfa",
    None: "#ffffff",
  };

  return {
    backgroundColor: allergenColors[normalizedAllergen] || "#ffffff",
    border: "none",
  };
};

export const getAllergenRiskLevel = (allergen) => {
  if (!allergen) return 0;

  // Normalize allergen string
  const normalizedAllergen = allergen
    .split(", ")
    .map((a) => a.trim())
    .sort()
    .join(", ");

  switch (normalizedAllergen) {
    case "Celery":
      return 1;
    case "Mustard":
    case "Celery, Mustard":
      return 2;
    case "Sesame":
    case "Mustard, Sesame":
      return 3;
    default:
      return 0;
  }
};

export const AllergenLegend = () => {
  const allergenColors = {
    None: "#ffffff", // White for no allergen
    Mustard: "#ffd9cc", // Vibrant yellow
    Sesame: "#bce453", // Emerald green
    Celery: "#60a5fa", // Soft blue
    "Mustard, Sesame": "#fb923c", // Warm orange
    "Mustard, Celery": "#a78bfa", // Light purple
    "Allergens in Room 1/Room 2": "#ffe5e5", // Softer red
    LAB_HOLD: "#fef08a", // Bright yellow
    QC_HOLD: "#f1bd99", // Vibrant orange
  };

  // Icon mapping with actual Heroicons
  const statusIcons = {
    completed: <CheckCircleIcon className="h-5 w-5 text-green-600" />,
    in_progress: <PlayCircleIcon className="h-5 w-5 text-blue-600" />,
    planned: <CalendarDaysIcon className="h-5 w-5 text-gray-600" />,
    LAB_HOLD: <BeakerIcon className="h-5 w-5 text-yellow-600" />,
    QC_HOLD: <ShieldExclamationIcon className="h-5 w-5 text-orange-600" />,
    default: <QuestionMarkCircleIcon className="h-5 w-5 text-gray-400" />,
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200 mb-6">
      <h3 className="text-lg font-semibold mb-3 text-gray-800">
        Allergen & Status Legend
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {/* Allergen and Room Colors */}
        {Object.entries(allergenColors).map(([key, color]) => (
          <div key={key} className="flex items-center gap-2">
            <div
              className="w-6 h-6 rounded"
              style={{
                backgroundColor: color,
                border:
                  key === "Allergens in Room 1/Room 2"
                    ? "2px solid #dc2626"
                    : "none",
              }}
            ></div>
            <span className="text-sm text-gray-900">{key}</span>
          </div>
        ))}

        {/* Status Icons */}
        {Object.entries(statusIcons).map(([status, icon]) => (
          <div key={status} className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center">
              {icon}
            </div>
            <span className="text-sm text-gray-900 capitalize">
              {status.replace("_", " ")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
